import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TransactionDetailModal = ({ isOpen, onClose, transaction }) => {
  if (!isOpen || !transaction) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeIcon = (type) => {
    const typeIcons = {
      'Invoice': 'FileText',
      'Invoice Payment': 'DollarSign',
      'Expense': 'Receipt',
      'Journal Entry': 'BookOpen',
      'Payroll': 'Users',
      'Bank Transaction': 'Landmark'
    };
    return typeIcons?.[type] || 'FileText';
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Transaction Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name={getTypeIcon(transaction?.type)} size={32} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-lg text-foreground">{transaction?.description}</h3>
              <p className="text-sm text-muted-foreground mt-1">{transaction?.type}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-secondary/10 text-secondary">
                  <Icon name="Tag" size={12} />
                  {transaction?.reference}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-muted/20 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Date</p>
              <p className="font-medium text-foreground">{formatDate(transaction?.date)}</p>
            </div>
            <div className="bg-muted/20 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Source</p>
              <p className="font-medium text-foreground">{transaction?.source}</p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-heading font-semibold text-base text-foreground">Account Information</h4>
            <div className="bg-muted/20 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account</span>
                <span className="font-medium text-foreground">{transaction?.account}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Account Code</span>
                <span className="font-medium text-foreground font-mono">{transaction?.accountCode}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-heading font-semibold text-base text-foreground">Transaction Amounts</h4>
            <div className="bg-muted/20 rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 divide-x divide-border">
                <div className="p-4">
                  <p className="text-sm text-muted-foreground mb-2">Debit</p>
                  <p className={`text-2xl font-bold ${
                    transaction?.debit > 0 ? 'text-primary' : 'text-muted-foreground'
                  }`}>
                    {transaction?.debit > 0 ? formatCurrency(transaction?.debit) : '-'}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-sm text-muted-foreground mb-2">Credit</p>
                  <p className={`text-2xl font-bold ${
                    transaction?.credit > 0 ? 'text-success' : 'text-muted-foreground'
                  }`}>
                    {transaction?.credit > 0 ? formatCurrency(transaction?.credit) : '-'}
                  </p>
                </div>
              </div>
              <div className="border-t border-border p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Running Balance</span>
                  <span className="text-xl font-bold text-foreground">{formatCurrency(transaction?.balance)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Transaction Reference</p>
                <p className="text-sm text-muted-foreground mt-1">
                  This transaction is linked to {transaction?.source}. View the original record for complete details.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/20">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={() => console.log('Navigate to source:', transaction?.source)}>
            <Icon name="ExternalLink" size={16} className="mr-2" />
            View Source
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;